import React, { useState, useRef, useEffect } from 'react';
import { McInput } from '@maersk-global/mds-react-wrapper/components-core/mc-input';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core/mc-icon';
import { COLORS, SPACING, FONT_SIZES, FONT_WEIGHTS, BORDER_RADIUS, SHADOWS, TRANSITIONS } from '../../constants/theme';

/**
 * Custom Dropdown Component
 * Kept as a custom listbox (not mc-select) because callers rely on `triggerStyle`,
 * the in-menu search and plain-text options; styled with MDS tokens.
 * Supports compact mode for toolbar usage
 */
const CustomDropdown = ({
  label,
  value,
  options,
  onChange,
  compact = false,
  hideLabel = false,
  triggerStyle = {},
  disabled = false,
  searchable = false,
  searchPlaceholder = 'Search...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredOptions = searchable && normalizedSearch
    ? options.filter((option) => String(option).toLowerCase().includes(normalizedSearch))
    : options;

  return (
    <div style={{ marginBottom: compact ? 0 : SPACING.xl }} ref={dropdownRef}>
      {!hideLabel && (
        <label style={{
          fontWeight: FONT_WEIGHTS.semibold,
          display: 'block',
          marginBottom: compact ? 4 : SPACING.sm,
          color: COLORS.text,
          fontSize: compact ? FONT_SIZES.sm : FONT_SIZES.base
        }}>
          {label}
        </label>
      )}
      
      <div style={{ position: 'relative' }}>
        <div
          onClick={() => {
            if (disabled) return;
            setIsOpen(!isOpen);
          }}
          style={{
            padding: compact ? `${SPACING.sm}px ${SPACING.md}px` : `${SPACING.md}px ${SPACING.lg}px`,
            fontSize: compact ? FONT_SIZES.sm : FONT_SIZES.base,
            borderRadius: BORDER_RADIUS.md,
            border: `1px solid ${isOpen ? COLORS.primary : COLORS.border}`,
            backgroundColor: disabled ? COLORS.lightGray : COLORS.white,
            cursor: disabled ? 'not-allowed' : 'pointer',
            fontWeight: FONT_WEIGHTS.medium,
            color: disabled ? COLORS.gray : COLORS.text,
            outline: 'none',
            transition: `all ${TRANSITIONS.base}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            userSelect: 'none',
            ...triggerStyle,
          }}
        >
          <span>{value}</span>
          <McIcon
            icon="chevron-down"
            size={compact ? '16' : '20'}
            color={disabled ? COLORS.gray : COLORS.text}
            style={{
              display: 'inline-flex',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: `transform ${TRANSITIONS.base}`,
              marginLeft: SPACING.sm,
              flexShrink: 0,
            }}
          />
        </div>
        
        {isOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            backgroundColor: COLORS.white,
            border: `1px solid ${COLORS.border}`,
            borderRadius: BORDER_RADIUS.md,
            boxShadow: SHADOWS.lg,
            zIndex: 1000,
            maxHeight: '280px',
            overflowY: 'auto'
          }}>
            {searchable && (
              <div style={{
                padding: `${SPACING.sm}px ${SPACING.sm}px ${SPACING.xs}px ${SPACING.sm}px`,
                borderBottom: `1px solid ${COLORS.borderLight}`,
                backgroundColor: COLORS.white,
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}>
                <McInput
                  label={searchPlaceholder}
                  hiddenlabel
                  type="search"
                  fit={compact ? 'small' : 'medium'}
                  value={searchTerm}
                  input={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  width="100"
                />
              </div>
            )}

            {filteredOptions.length === 0 ? (
              <div style={{
                padding: `${SPACING.md}px ${SPACING.lg}px`,
                color: COLORS.gray,
                fontSize: FONT_SIZES.base,
                cursor: 'default',
              }}>
                No results
              </div>
            ) : filteredOptions.map((option) => (
              <div
                key={option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                style={{
                  padding: `${SPACING.md}px ${SPACING.lg}px`,
                  cursor: 'pointer',
                  backgroundColor: value === option ? COLORS.primaryWeak : COLORS.white,
                  color: COLORS.text,
                  fontWeight: value === option ? FONT_WEIGHTS.semibold : FONT_WEIGHTS.medium,
                  fontSize: FONT_SIZES.base,
                  transition: `all ${TRANSITIONS.fast}`,
                  borderBottom: `1px solid ${COLORS.borderLight}`
                }}
                onMouseEnter={(e) => {
                  if (value !== option) {
                    e.target.style.backgroundColor = COLORS.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (value !== option) {
                    e.target.style.backgroundColor = COLORS.white;
                  }
                }}
              >
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDropdown;
